import { cn } from '@/lib/utils';

interface RubyAvatarProps {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  /**
   * Use on yellow `bg-primary` surfaces. Default gem fill is also `--primary`, which disappears on a primary button;
   * this variant uses a white fill with the same white facet strokes as the default gem (facets blend into the fill — no dark lines).
   */
  variant?: 'default' | 'on-primary';
  /** Larger gem inside the same outer size (less empty margin in the SVG). */
  iconFill?: boolean;
}

const sizeClasses = {
  xs: 'w-6 h-6',
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-16 h-16',
};

export function RubyAvatar({ size = 'md', className, variant = 'default', iconFill = false }: RubyAvatarProps) {
  const onPrimary = variant === 'on-primary';
  const fill = onPrimary ? 'white' : 'hsl(var(--primary))';
  const gemScale = iconFill ? 0.97 : 0.85;

  return (
    <div className={cn(
      sizeClasses[size],
      'rounded-full overflow-hidden shrink-0',
      onPrimary ? 'shadow-none bg-transparent' : 'bg-primary/10 shadow-lg shadow-primary/20',
      className
    )}>
      <svg viewBox="0 0 64 64" className="w-full h-full block" preserveAspectRatio="xMidYMid meet">
        <g transform={`translate(32 32) scale(${gemScale}) translate(-32 -32)`}>
          {/* Centered gem with safe inset — white strokes on both variants (on-primary: facets match white fill, no dark lines) */}
          <polygon 
            points="14,24 24,14 40,14 50,24 32,50" 
            fill={fill}
            stroke="white"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          
          {/* Top horizontal facet line */}
          <path 
            d="M14,24 L24,28 L40,28 L50,24" 
            stroke="white"
            strokeWidth="1.5" 
            fill="none"
            strokeLinejoin="round"
          />
          
          {/* Vertical facet lines */}
          <path d="M24,14 L24,28 L32,50" stroke="white" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
          <path d="M40,14 L40,28 L32,50" stroke="white" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
          
          {/* Center vertical line */}
          <path d="M32,28 L32,50" stroke="white" strokeWidth="1.5" fill="none" />
        </g>
      </svg>
    </div>
  );
}
